import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CACHE_KEYS } from '../contants/CACHE_KEYS';
import { KeyValuePair } from '../models/common/keyValuePair';
import { PageOption } from '../models/common/pageOption';
import { PaginatedList } from '../models/common/paginatedList';
import { RequestResult } from '../models/common/requestResult';
import { ITripSearchResult, Trip } from '../models/trips/trip';
import { TripFilter } from '../models/trips/tripFilter';
import { TripSelectDetail } from '../models/trips/tripSelectDetail';
import { TripType } from '../models/trips/tripType';
import { CacheService } from './cache.service';
import { CommonService } from './common.service';
import { LogService } from './log.service';
import { MessagesService } from './messages.service';
import { TripContainerDto } from '../models/trips/tripContainer';

@Injectable()
export class TripService {
	private origin: string = environment.API_BASE_URL;

	constructor(
		private _http: HttpClient,
		private _logService: LogService,
		private _messagesService: MessagesService,
		private _common: CommonService,
		private _cacheService: CacheService
	) { }

	getTrip(id: string): Observable<Trip> {
		return this._http.get<Trip>(`${this.origin}/api/trips/${id}`)
			.pipe(
				tap(x => this.log(`getTrip: id=${id}`)),
				catchError(this._logService.handleError<Trip>('getTrip'))
			);
	}

	search(filter: PageOption<TripFilter>): Observable<PaginatedList<Trip>> {
		let param = this._common.objectToQueryParam(filter);
		return this._http.get<PaginatedList<Trip>>(`${this.origin}/api/trips${param}`)
			.pipe(
				tap(x => this.log(`getTripsList param = ${param}`)),
				catchError(this._logService.handleError<PaginatedList<Trip>>('getTripsList'))
			);
	}

	searchV2(filter: PageOption<TripFilter>): Observable<PaginatedList<ITripSearchResult>> {
		let param = this._common.objectToQueryParam(filter);
		return this._http.get<PaginatedList<ITripSearchResult>>(`${this.origin}/api/v2/trips${param}`)
			.pipe(
				tap(x => this.log(`getTripsListV2 param = ${param}`)),
				catchError(this._logService.handleError<PaginatedList<ITripSearchResult>>('getTripsListV2'))
			);
	}

	searchLimited(filter: PageOption<TripFilter>): Observable<PaginatedList<Trip>> {
		let param = this._common.objectToQueryParam(filter);
		return this._http.get<PaginatedList<Trip>>(`${this.origin}/api/menus/trips2${param}`)
			.pipe(
				tap(x => this.log(`getTripsList param = ${param}`)),
				catchError(this._logService.handleError<PaginatedList<Trip>>('getTripsList'))
			);
	}

	getTripsDropdownList(clientId?: number): Observable<TripSelectDetail[]> {
		var cacheKey = CACHE_KEYS.tripSelection;
		var url = `${this.origin}/api/menus/trips?clientId=`;

		if (clientId == null || clientId == undefined || isNaN(Number(clientId))){
			url += 0;
		}
		else{
			cacheKey += `_${clientId}`;
			url += clientId;
		}

		let value = this._cacheService.get(cacheKey)
		if (value != null) {
			return of(value);
		}

		return this._http.get<TripSelectDetail[]>(url)
			.pipe(
				tap(x => {
					this.log(`getTripsDropdownList`);
					this._cacheService.add(cacheKey, x, 180);
				}),
				catchError(this._logService.handleError<TripSelectDetail[]>('getTripsDropdownList'))
			);
	}

	getContainersDropdownList(tripId: number): Observable<KeyValuePair[]> {
		let cacheKey = `${CACHE_KEYS.containerSelection}_${tripId}`;
		let value = this._cacheService.get(cacheKey)
		if (value != null) {
			return of(value);
		}

		return this._http.get<KeyValuePair[]>(`${this.origin}/api/menus/containers/${tripId}`)
			.pipe(
				tap(x => {
					this.log(`getContainersDropdownList`);
					this._cacheService.add(cacheKey, x, 180);
				}),
				catchError(this._logService.handleError<KeyValuePair[]>('getContainersDropdownList'))
			);
	}

	getContainersDropdownListV2(tripId: number): Observable<TripContainerDto[]> {
		let cacheKey = `${CACHE_KEYS.containerSelection}_${tripId}`;
		let value = this._cacheService.get(cacheKey)
		if (value != null) {
			return of(value);
		}

		return this._http.get<TripContainerDto[]>(`${this.origin}/api/trips/${tripId}/containers`)
			.pipe(
				tap(x => {
					this.log(`getContainersDropdownListV2`);
					this._cacheService.add(cacheKey, x, 180);
				}),
				catchError(this._logService.handleError<TripContainerDto[]>('getContainersDropdownListV2'))
			);
	}

	getTripRates(): Observable<KeyValuePair[]> {
		let cacheKey = CACHE_KEYS.tripRates;
		let value = this._cacheService.get(cacheKey)
		if (value != null) {
			return of(value);
		}

		return this._http.get<KeyValuePair[]>(`${this.origin}/api/trips/rates`)
			.pipe(
				tap(x => {
					this.log(`getTripRates`);
					this._cacheService.add(cacheKey, x, 180);
				}),
				catchError(this._logService.handleError<KeyValuePair[]>('getTripRates'))
			);
	}

	addTrip(trip: Trip): Observable<RequestResult<Trip>> {
		this._cacheService.remove(CACHE_KEYS.tripSelection);
		this._cacheService.remove(CACHE_KEYS.tripRates);

		return this._http.post<RequestResult<Trip>>(`${this.origin}/api/trips`, JSON.stringify(trip))
			.pipe(
				tap(x => this.log(`addTrip: id=${trip.billOfLading}`)),
				catchError(this._logService.handleError<RequestResult<Trip>>('addTrip'))
			);
	}

	updateTrip(trip: Trip): Observable<RequestResult<Trip>> {
		this._cacheService.remove(`${CACHE_KEYS.containerSelection}_${trip.id}`);
		this._cacheService.remove(CACHE_KEYS.tripRates);

		return this._http.put<RequestResult<Trip>>(`${this.origin}/api/trips/${trip.id}`, JSON.stringify(trip))
			.pipe(
				tap(x => this.log(`updateTrip: id=${trip.id}`)),
				catchError(this._logService.handleError<RequestResult<Trip>>('updateTrip'))
			);
	}

	deleteTrip(tripId: number): Observable<RequestResult<Trip>> {
		this._cacheService.remove(CACHE_KEYS.tripSelection);
		this._cacheService.remove(`${CACHE_KEYS.containerSelection}_${tripId}`);
		this._cacheService.remove(CACHE_KEYS.tripRates);

		return this._http.delete<RequestResult<Trip>>(`${this.origin}/api/trips/${tripId}`)
			.pipe(
				tap(x => this.log(`deleteTrip: id=${tripId}`)),
				catchError(this._logService.handleError<RequestResult<Trip>>('deleteTrip'))
			);
	}

	getTripTypes(): Observable<TripType[]> {
		var cacheKey = CACHE_KEYS.tripTypes;
		var url = `${this.origin}/api/trip-types`;

		let value = this._cacheService.get(cacheKey)
		if (value != null) {
			return of(value);
		}

		return this._http.get<TripType[]>(url)
			.pipe(
				tap(x => {
					this.log(`getTripTypes`);
					this._cacheService.add(cacheKey, x, 10 * 60 * 1000);
				}),
				catchError(this._logService.handleError<TripType[]>('getTripTypes'))
			);
	}

	private log(message: string) {
		this._messagesService.add('TripService: ' + message);
	}

}



// WEBPACK FOOTER //
// ./src/app/services/trip.service.ts